import { type ForwardedRef, forwardRef, useId, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  type DataTestId,
  type StylingProps,
  type AriaLabelingProps,
  type DOMProps,
} from '@dynatrace/strato-components/core';
import { ResetIcon } from '@dynatrace/strato-icons';

import { Tooltip } from '../../overlays/tooltip/Tooltip.js';

//#region Typings
/**
 * Accepted properties for the Chip Button.
 * @public
 */
export interface ResetButtonProps
  extends AriaLabelingProps,
    DOMProps,
    StylingProps,
    DataTestId {
  /** Handler that is called when the ResetButton is clicked. */
  onClick: () => void;
}
//#endregion Typings

/**
 * Component which allows resetting the values of the FilterBar items.
 * @public
 */
export const ResetButton = /* @__PURE__ */ forwardRef(
  (props: ResetButtonProps, forwardedRef: ForwardedRef<HTMLButtonElement>) => {
    const {
      onClick,
      'aria-labelledby': ariaLabelledByProp,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: ResetButtonProps = props;

    const intl = useIntl();
    const resetText = intl.formatMessage({
      defaultMessage: 'Reset filters',
      id: '21mG229oVCMPPsYY',
      description: 'Shown for the reset button of the FilterBar.',
    });

    const tooltipId = useId();

    const ariaLabelledBy = useMemo(
      () =>
        tooltipId || ariaLabelledByProp
          ? `${tooltipId} ${ariaLabelledByProp ?? ''}`
          : undefined,
      [ariaLabelledByProp, tooltipId],
    );

    return (
      <Tooltip id={tooltipId} text={resetText}>
        <Button
          data-testid={dataTestId}
          ref={forwardedRef}
          variant="default"
          onClick={onClick}
          aria-label={resetText}
          aria-labelledby={ariaLabelledBy}
          className={consumerClassName}
          style={consumerStyle}
          {...remainingProps}
        >
          <Button.Prefix>
            <ResetIcon />
          </Button.Prefix>
        </Button>
      </Tooltip>
    );
  },
);

(ResetButton as typeof ResetButton & { displayName: string }).displayName =
  'FilterBar.ResetButton';
